import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { form, FormField as SignalFormField, required } from '@angular/forms/signals';
import {
  SignalCheckbox,
  SignalFormDirective,
  SignalRadioGroup,
  SignalTextField,
  type SignalRadioOption,
} from '@vallift/ngl';
import type { FormField, RatingConfig } from '../../../../models/built-form';

type RatingScaleValue = '5' | '10';

type RatingScale = 5 | 10;

interface RatingConfigModel {
  label: string;
  helpText: string;
  max: RatingScaleValue;
  required: boolean;
}

@Component({
  selector: 'app-rating-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalTextField,
    SignalCheckbox,
    SignalRadioGroup,
    SignalFormDirective,
  ],
  templateUrl: './rating-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RatingConfigViewComponent {
  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly scaleOptions: readonly SignalRadioOption<RatingScaleValue>[] = [
    { value: '5', label: 'Up to 5 Stars' },
    { value: '10', label: 'Up to 10' },
  ];

  readonly model = signal<RatingConfigModel>({
    label: '',
    helpText: '',
    max: '5',
    required: false,
  });

  readonly configForm = form(this.model, (schema) => {
    required(schema.label);
  });

  private lastFieldId: string | null = null;
  private suppressEmit = false;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.type !== 'rating') {
        return;
      }
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      const validation = next.validation ?? {};
      const config = this.readRatingConfig(next);
      const max = config.max ?? 5;
      this.model.set({
        label: next.label,
        helpText: next.helpText ?? '',
        max: max === 10 ? '10' : '5',
        required: next.required ?? validation.required ?? false,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }

      const max = this.toScale(draft.max);
      const nextConfig: RatingConfig = { max };

      this.fieldChange.emit({
        ...field,
        type: 'rating',
        label: draft.label.trim() || field.label,
        helpText: draft.helpText.trim() || undefined,
        config: nextConfig,
        required: draft.required,
        disabled: undefined,
        validation: {
          required: draft.required,
          min: 1,
          max,
        },
        options: undefined,
        headingLevel: undefined,
        spacerSize: undefined,
      });
    });
  }

  onScaleChange(value: RatingScaleValue): void {
    this.model.update((model) => ({
      ...model,
      max: this.toScale(value) === 5 ? '5' : '10',
    }));
  }

  private toScale(value: RatingScaleValue | RatingScale): RatingScale {
    return value === 5 || value === '5' ? 5 : 10;
  }

  private readRatingConfig(field: FormField): RatingConfig {
    const config = field.config;
    const fromConfig =
      config && 'max' in config && (config.max === 5 || config.max === 10)
        ? config.max
        : undefined;
    if (fromConfig != null) {
      return { max: fromConfig };
    }
    const fromValidation = field.validation?.max;
    if (fromValidation === 5 || fromValidation === 10) {
      return { max: fromValidation };
    }
    return {};
  }
}
